import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEventStore } from '../../store/eventStore';
import { useFamilyStore } from '../../store/familyStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import { DateType, Priority, RecurringFrequency, RecurringPattern } from '../../types/calendar';
import { Icon } from '../../utils/icons';
import { formatDateString } from '../../utils/time';
import { useToastStore } from '../../store/toastStore';

type EventType = 'importantDate' | 'timedEvent';

export default function QuickCreateEvent() {
  const navigate = useNavigate();
  const { family } = useFamilyStore();
  const { createEvent, isCreating } = useEventStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { showToast } = useToastStore();
  
  const [eventType, setEventType] = useState<EventType | null>(null);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [dateType, setDateType] = useState<DateType>('regular');
  const [priority, setPriority] = useState<Priority>('medium');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<RecurringFrequency>('weekly');
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [reminderDays, setReminderDays] = useState<number[]>([]);
  const [isAllDay, setIsAllDay] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Initialize with smart defaults
  useEffect(() => {
    const now = new Date();
    const today = formatDateString(now);
    const currentHour = now.getHours();
    const nextHour = (currentHour + 1) % 24;
    
    setStartDate(today);
    setEndDate(today);
    setStartTime(`${String(currentHour).padStart(2, '0')}:00`);
    setEndTime(`${String(nextHour).padStart(2, '0')}:00`);
    
    // Pre-select current user if available
    if (family) {
      const currentUserId = getCurrentUserId();
      if (currentUserId && family.members.find(m => m.userId === currentUserId)) {
        setMemberIds([currentUserId]);
      }
    }
  }, [family]);

  const getCurrentUserId = (): string | null => {
    try {
      const { useAuthStore } = require('../../store/authStore');
      const { idToken } = useAuthStore.getState();
      if (!idToken) return null;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload.sub;
    } catch {
      return null;
    }
  };

  const handleEventTypeSelect = (type: EventType) => {
    setEventType(type);
    // Pre-fill times for timed events
    if (type === 'timedEvent') {
      const today = new Date();
      const currentHour = today.getHours();
      const startTime = `${currentHour.toString().padStart(2, '0')}:00`;
      const endTime = `${(currentHour + 1).toString().padStart(2, '0')}:00`;
      setStartTime(startTime);
      setEndTime(endTime);
    } else {
      // Clear times for important dates
      setStartTime('');
      setEndTime('');
    }
  };

  const toggleMember = (memberId: string) => {
    setMemberIds(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const toggleReminderDay = (days: number) => {
    setReminderDays(prev => 
      prev.includes(days)
        ? prev.filter(d => d !== days)
        : [...prev, days].sort((a, b) => b - a)
    );
  };

  const validateForm = (): boolean => {
    if (!title.trim()) return false;
    if (!startDate) return false;
    
    if (eventType === 'timedEvent') {
      if (!endDate) return false;
      // Times are only required if not all-day
      if (!isAllDay) {
        if (!startTime || !endTime) return false;
      }
      if (memberIds.length === 0) return false;
    }
    // For important dates, memberIds are optional
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!eventType) {
      showToast('Please select an event type', 'error');
      return;
    }

    if (!validateForm()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      let startDateTime: Date;
      let endDateTime: Date;

      if (eventType === 'importantDate') {
        // For important dates, use date-only (start of day to end of day)
        const [year, month, day] = startDate.split('-').map(Number);
        startDateTime = new Date(year, month - 1, day, 0, 0, 0);
        endDateTime = new Date(year, month - 1, day, 23, 59, 59);
      } else {
        // For timed events
        if (isAllDay) {
          // All-day events: start of day to end of day
          const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
          startDateTime = new Date(startYear, startMonth - 1, startDay, 0, 0, 0);
          endDateTime = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);
        } else {
          // Timed events with specific times
          startDateTime = new Date(`${startDate}T${startTime}`);
          endDateTime = new Date(`${endDate}T${endTime}`);
          
          // Ensure end time is after start time
          if (endDateTime <= startDateTime) {
            endDateTime.setHours(startDateTime.getHours() + 1);
          }
        }
      }

      // Build recurring pattern if recurring
      const recurringPattern: RecurringPattern | undefined = isRecurring ? {
        frequency: recurringFrequency,
        interval: recurringInterval,
      } : undefined;

      await createEvent({
        title: title.trim(),
        description: description.trim() || undefined,
        startTime: startDateTime,
        endTime: endDateTime,
        memberIds: eventType === 'importantDate' ? memberIds : (memberIds.length > 0 ? memberIds : []),
        location: location.trim() || undefined,
        dateType: eventType === 'importantDate' ? dateType : 'regular',
        priority,
        reminderDays: reminderDays.length > 0 ? reminderDays : undefined,
        isRecurring,
        recurringPattern,
        isAllDay: eventType === 'timedEvent' ? isAllDay : false,
        isImportantDate: eventType === 'importantDate',
      });
      showToast('Event created', 'success');

      // Navigate back to today view
      navigate('/mobile/today');
    } catch (error: any) {
      showToast(error?.message || 'Failed to create event', 'error');
    }
  };

  // Auto-enable recurring for birthdays/anniversaries
  useEffect(() => {
    if (dateType === 'birthday' || dateType === 'anniversary') {
      setIsRecurring(true);
      setRecurringFrequency('yearly');
    }
  }, [dateType]);

  if (!family) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className={tokens.text.muted}>Loading family...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto scrollable pb-4">
        {/* Header */}
        <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10 px-4 py-2 safe-area-inset-top`}>
          <h1 className={`text-2xl font-bold ${tokens.text.primary} font-serif`}>Create Event</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-3 py-2 space-y-3">
          {/* Event Type Selection */}
          {!eventType && (
            <div className="space-y-3">
              <h3 className={`text-lg font-semibold ${tokens.text.primary} mb-2`}>
                What type of event are you creating?
              </h3>
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => handleEventTypeSelect('importantDate')}
                  disabled={isCreating}
                  className={`${tokens.card.bg} p-4 rounded-landing border-2 ${tokens.border.light} transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 min-h-[100px] flex flex-col items-center justify-center gap-2`}
                >
                  <div className="text-3xl">📋</div>
                  <div className={`${tokens.text.primary} font-bold text-base`}>Important Date</div>
                  <div className={`${tokens.text.muted} text-sm text-center`}>
                    Birthdays, anniversaries, holidays, milestones
                  </div>
                  <div className={`${tokens.text.muted} text-xs text-center opacity-75`}>
                    No specific time needed
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleEventTypeSelect('timedEvent')}
                  disabled={isCreating}
                  className={`${tokens.card.bg} p-4 rounded-landing border-2 ${tokens.border.light} transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 min-h-[100px] flex flex-col items-center justify-center gap-2`}
                >
                  <div className="text-3xl">⏰</div>
                  <div className={`${tokens.text.primary} font-bold text-base`}>Event with Time</div>
                  <div className={`${tokens.text.muted} text-sm text-center`}>
                    Appointments, meetings, activities
                  </div>
                  <div className={`${tokens.text.muted} text-xs text-center opacity-75`}>
                    Start and end times required
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Basic Fields - Only show if event type is selected */}
          {eventType && (
            <>
              {/* Title */}
              <div>
                <label htmlFor="title" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                  placeholder="Event title"
                  autoFocus
                />
              </div>

              {/* Date/Time Fields - Different for each event type */}
              {eventType === 'importantDate' ? (
                <>
                  {/* Date */}
                  <div>
                    <label htmlFor="date" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                      Date *
                    </label>
                    <input
                      type="date"
                      id="date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setEndDate(e.target.value);
                      }}
                      required
                      className={`w-full px-4 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                    />
                  </div>

                  {/* Date Type */}
                  <div>
                    <label htmlFor="dateType" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                      Date Type (optional)
                    </label>
                    <select
                      id="dateType"
                      value={dateType}
                      onChange={(e) => setDateType(e.target.value as DateType)}
                      className={`w-full px-4 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                    >
                      <option value="regular">Regular</option>
                      <option value="birthday">Birthday</option>
                      <option value="anniversary">Anniversary</option>
                      <option value="visit">Guest Visit</option>
                      <option value="holiday">Holiday</option>
                      <option value="school">School Event</option>
                      <option value="medical">Medical Appointment</option>
                      <option value="milestone">Milestone</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {/* Date + Time */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label htmlFor="date" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                        Date *
                      </label>
                      <input
                        type="date"
                        id="date"
                        value={startDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setStartDate(newStartDate);
                          if (newStartDate > endDate) {
                            setEndDate(newStartDate);
                          }
                        }}
                        required
                        className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                      />
                    </div>
                    {!isAllDay && (
                      <>
                        <div>
                          <label htmlFor="startTime" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                            Start *
                          </label>
                          <input
                            type="time"
                            id="startTime"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            required
                            className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                          />
                        </div>
                        <div>
                          <label htmlFor="endTime" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                            End *
                          </label>
                          <input
                            type="time"
                            id="endTime"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            required
                            className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* End Date for multi-day events */}
                  {isAllDay && (
                    <div>
                      <label htmlFor="endDate" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                        End Date *
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          setEndDate(newEndDate);
                          if (newEndDate < startDate) {
                            setStartDate(newEndDate);
                          }
                        }}
                        required
                        min={startDate}
                        className={`w-full px-4 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                      />
                    </div>
                  )}

                  {/* All Day Toggle */}
                  <div className={`flex items-start gap-3 p-3 rounded-landing border-2 ${tokens.border.light} ${tokens.button.secondary}`}>
                    <input
                      type="checkbox"
                      id="isAllDay"
                      checked={isAllDay}
                      onChange={(e) => {
                        const allDay = e.target.checked;
                        setIsAllDay(allDay);
                        if (allDay) {
                          setStartTime('');
                          setEndTime('');
                        } else {
                          const today = new Date();
                          const currentHour = today.getHours();
                          setStartTime(`${currentHour.toString().padStart(2, '0')}:00`);
                          setEndTime(`${(currentHour + 1).toString().padStart(2, '0')}:00`);
                        }
                      }}
                      className="w-6 h-6 touch-target cursor-pointer mt-0.5"
                    />
                    <div className="flex-1">
                      <label htmlFor="isAllDay" className={`${tokens.text.primary} text-sm font-medium cursor-pointer block mb-1`}>
                        All Day Event
                      </label>
                      <p className={`${tokens.text.muted} text-xs leading-relaxed`}>
                        {isAllDay 
                          ? startDate === endDate
                            ? 'Event will span the entire day (no specific times)'
                            : `Event will span from ${startDate} to ${endDate} (all day)`
                          : 'Check this to make the event span entire day(s) without specific times'}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Family Members */}
              <div>
                <label className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                  Family Members {eventType === 'timedEvent' ? '*' : ''} {eventType === 'importantDate' && '(optional)'}
                </label>
                {eventType === 'importantDate' && (
                  <p className={`${tokens.text.muted} text-xs mb-2 opacity-75`}>
                    Family members are optional - useful for friend's birthdays, holidays, etc.
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {family.members.map((member) => {
                    const isSelected = memberIds.includes(member.userId);
                    return (
                      <button
                        key={member.userId}
                        type="button"
                        onClick={() => toggleMember(member.userId)}
                        className={`relative flex flex-col items-center gap-2 p-3 rounded-landing border-2 transition-all min-h-[80px] min-w-[80px] touch-manipulation active:scale-95 ${
                          isSelected
                            ? `${tokens.border.accent} ${tokens.button.secondary} scale-105 shadow-sm`
                            : `${tokens.border.light} ${tokens.card.bg}`
                        }`}
                      >
                        <div className="relative">
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                            style={{ backgroundColor: member.color }}
                          >
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          {member.avatar && (
                            <img
                              src={member.avatar}
                              alt={member.name}
                              className="absolute inset-0 w-12 h-12 rounded-full object-cover border-2 z-10"
                              style={{ borderColor: member.color }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          {isSelected && (
                            <div className={`absolute -top-1 -right-1 w-6 h-6 ${tokens.button.primary} rounded-full flex items-center justify-center border-2 ${tokens.card.bg} z-20`}>
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className={`text-[10px] font-semibold ${isSelected ? tokens.text.accent : tokens.text.primary}`}>
                          {member.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {eventType === 'timedEvent' && memberIds.length === 0 && (
                  <p className="mt-2 text-xs text-red-500">Please select at least one family member</p>
                )}
              </div>

              {/* Advanced Settings */}
              {!showAdvanced && (
                <button
                  type="button"
                  onClick={() => setShowAdvanced(true)}
                  disabled={isCreating}
                  className={`w-full ${tokens.button.secondary} border ${tokens.border.light} px-6 py-3 rounded-landing text-sm font-semibold transition-all active:scale-95 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
                >
                  + Add More Details
                </button>
              )}

              {showAdvanced && (
                <div className="space-y-3">
                  {/* Location */}
                  <div>
                    <label htmlFor="location" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                      Location (optional)
                    </label>
                    <input
                      type="text"
                      id="location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className={`w-full px-4 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                      placeholder="Event location"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                      Description (optional)
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className={`w-full px-4 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base resize-none ${tokens.card.bg} ${tokens.text.primary}`}
                      placeholder="Add any additional details..."
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label htmlFor="priority" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                      Priority
                    </label>
                    <select
                      id="priority"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as Priority)}
                      className={`w-full px-4 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  {/* Recurring Event */}
                  <div>
                    <label className={`flex items-center gap-2 ${tokens.text.primary} text-sm font-medium mb-2`}>
                      <input
                        type="checkbox"
                        checked={isRecurring || dateType === 'birthday' || dateType === 'anniversary'}
                        onChange={(e) => {
                          const recurring = e.target.checked || dateType === 'birthday' || dateType === 'anniversary';
                          setIsRecurring(recurring);
                          if (dateType === 'birthday' || dateType === 'anniversary') {
                            setRecurringFrequency('yearly');
                          }
                        }}
                        disabled={dateType === 'birthday' || dateType === 'anniversary'}
                        className="w-5 h-5 touch-target disabled:opacity-50"
                      />
                      <span>Recurring Event {dateType === 'birthday' || dateType === 'anniversary' ? '(Auto-enabled)' : ''}</span>
                    </label>
                    {(isRecurring || dateType === 'birthday' || dateType === 'anniversary') && (
                      <div className="ml-7 space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label htmlFor="recurringFrequency" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                              Frequency
                            </label>
                            <select
                              id="recurringFrequency"
                              value={recurringFrequency}
                              onChange={(e) => setRecurringFrequency(e.target.value as RecurringFrequency)}
                              disabled={dateType === 'birthday' || dateType === 'anniversary'}
                              className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary} disabled:opacity-50`}
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                              <option value="yearly">Yearly</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="recurringInterval" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
                              Occurs every {recurringInterval} {recurringFrequency === 'daily' ? 'day' : recurringFrequency === 'weekly' ? 'week' : recurringFrequency === 'monthly' ? 'month' : 'year'}
                            </label>
                            <select
                              id="recurringInterval"
                              value={recurringInterval}
                              onChange={(e) => setRecurringInterval(parseInt(e.target.value) || 1)}
                              className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                <option key={num} value={num}>
                                  {num}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Reminder Days */}
                  <div>
                    <label className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
                      Reminder Days (optional)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[7, 3, 1].map((days) => {
                        const isSelected = reminderDays.includes(days);
                        return (
                          <button
                            key={days}
                            type="button"
                            onClick={() => toggleReminderDay(days)}
                            className={`px-3 py-2 rounded-landing text-xs font-semibold transition-all touch-target min-h-[44px] ${
                              isSelected
                                ? `${tokens.button.primary} ${tokens.text.inverse} border-2 ${tokens.border.accent}`
                                : `${tokens.button.secondary} border ${tokens.border.light}`
                            }`}
                          >
                            {days} {days === 1 ? 'day' : 'days'} before
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/mobile/today')}
                  className={`flex-1 px-6 py-3 ${tokens.button.secondary} font-semibold transition-colors min-h-[48px]`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !validateForm()}
                  className={`flex-1 px-6 py-3 ${tokens.button.primary} font-semibold transition-colors min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isCreating ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
